import { IClientGridRow } from '@/Interfaces/Client.interface';
import { IControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import { IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import { transformAdvertiserFilters } from '@/helpers/advertisers';
import { transformAgenciesFilters } from '@/helpers/agencies';
import { formatClientsListResponseData, formatControlOptions, transformClientsFilters } from '@/helpers/clients';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/helpers/forecaster/slice';
import { RootState } from '@/store';
import { fetchAdvertisers } from '@/store/api/advertisers.api';
import { fetchAgencies } from '@/store/api/agencies.api';
import { fetchClientsList } from '@/store/api/clients.api';
import { SearchClientsFilters } from '@/types/clients.types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

enum actionPrefix {
  FETCH_CLIENTS = 'clientsSlice/fetchClients',
  FETCH_ADVERTISERS = 'clientsSlice/fetchAdvertisers',
  FETCH_AGENCIES = 'clientsSlice/fetchAgencies',
}

interface IState {
  data: IClientGridRow[];
  dataRequestId: string | null;
  selectedClient: IClientGridRow | null;
  total: number;
  filters: SearchClientsFilters;
  agencies: IControlOption[];
  agenciesRequestId: string | null;
  advertisers: IControlOption[];
  advertisersRequestId: string | null;
}

const initialState: IState = {
  data: [],
  dataRequestId: null,
  total: 0,
  selectedClient: null,
  filters: {
    page: 1,
    limit: 15,
    search: '',
    sort: [{ field: 'name', sort: 'asc' }],
    isActive: null,
    whitelabels: [],
  },
  agencies: [],
  agenciesRequestId: null,
  advertisers: [],
  advertisersRequestId: null,
};

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    setSelectedClient: (state, { payload }: { payload: IClientGridRow }) => {
      state.selectedClient = payload;
    },
    clearSelectedClient: state => {
      state.selectedClient = null;
    },
    setFilters: (state, { payload }: { payload: Partial<SearchClientsFilters> }) => {
      state.filters = { ...state.filters, ...payload };
    },
    resetClientsPageState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getClients.fulfilled, (state, { meta, payload }) => {
        if (state.dataRequestId !== meta.requestId) return;
        state.dataRequestId = null;
        state.data = payload.data;
        state.total = payload.total;
        if (!payload.data.length && state.filters.page > 1) {
          state.filters.page = 1;
        }
      })
      .addCase(getClients.pending, pendingCaseHandler('data'))
      .addCase(getClients.rejected, rejectedCaseHandler('data'))
      .addCase(getAdvertisers.fulfilled, fulfilledCaseHandler('advertisers'))
      .addCase(getAdvertisers.pending, pendingCaseHandler('advertisers'))
      .addCase(getAdvertisers.rejected, rejectedCaseHandler('advertisers'))
      .addCase(getAgencies.fulfilled, fulfilledCaseHandler('agencies'))
      .addCase(getAgencies.pending, pendingCaseHandler('agencies'))
      .addCase(getAgencies.rejected, rejectedCaseHandler('agencies'));
  },
});

export default clientsSlice.reducer;

export const { setSelectedClient, clearSelectedClient, setFilters, resetClientsPageState } = clientsSlice.actions;

export const getClients = createAsyncThunk<
  { data: IClientGridRow[]; total: number },
  undefined,
  IRejectValueThunkConfig
>(actionPrefix.FETCH_CLIENTS, async (_, { rejectWithValue, getState }) => {
  const {
    clients: { filters },
  } = getState() as RootState;

  const transformedFilters = transformClientsFilters(filters);

  try {
    const response = await fetchClientsList(transformedFilters);
    return formatClientsListResponseData(response);
  } catch (err) {
    if (err instanceof Error) {
      return rejectWithValue(err);
    }
    return rejectWithValue(new Error('An unknown error occurred'));
  }
});

export const getAdvertisers = createAsyncThunk<IControlOption[], IWhitelabelControlOption, IRejectValueThunkConfig>(
  actionPrefix.FETCH_ADVERTISERS,
  async (whitelabel, { rejectWithValue }) => {
    const filters = transformAdvertiserFilters({
      whitelabels: [whitelabel],
      clients: [],
      sort: null,
      active: true,
      isArchived: false,
      limit: 1000,
      page: 1,
    });

    try {
      const response = await fetchAdvertisers(filters);
      return formatControlOptions(response);
    } catch (err) {
      if (err instanceof Error) {
        return rejectWithValue(err);
      }
      return rejectWithValue(new Error('An unknown error occurred'));
    }
  },
);

export const getAgencies = createAsyncThunk<IControlOption[], IWhitelabelControlOption, IRejectValueThunkConfig>(
  actionPrefix.FETCH_AGENCIES,
  async (whitelabel, { rejectWithValue }) => {
    const filters = transformAgenciesFilters({
      whitelabels: [whitelabel],
      clients: [],
      sort: null,
      limit: 1000,
      page: 1,
    });

    try {
      const response = await fetchAgencies(filters);
      return formatControlOptions(response);
    } catch (err) {
      if (err instanceof Error) {
        return rejectWithValue(err);
      }
      return rejectWithValue(new Error('An unknown error occurred'));
    }
  },
);
